import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';
import { aspectRatioOptions } from '../data/vehicleData.js';
import ImageModal from './ImageModal.jsx';

const { FiDownload, FiShare2, FiCopy, FiEye, FiAlertCircle, FiLoader, FiMaximize2 } = FiIcons;

const ImagePreview = ({ imageResult, isGenerating, prompt, aspectRatio, textOverlays }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const aspectRatioInfo = aspectRatioOptions.find(option => option.id === aspectRatio);
  const visibleOverlays = textOverlays?.filter(overlay => overlay.visible) || [];

  const handleDownload = () => {
    if (imageResult && imageResult.imageUrl) {
      const link = document.createElement('a');
      link.href = imageResult.imageUrl;
      link.download = `vehicle-marketing-${Date.now()}.${imageResult.mimeType?.split('/')[1] || 'png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyPrompt = async () => {
    if (prompt) {
      try {
        await navigator.clipboard.writeText(prompt);
        alert('Prompt copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy prompt:', err);
      }
    }
  };

  const handleShare = async () => {
    if (navigator.share && imageResult && imageResult.imageUrl) {
      try {
        await navigator.share({
          title: 'Vehicle Marketing Image',
          text: 'Check out this marketing image generated for our dealership!',
          url: imageResult.imageUrl
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    }
  };

  const getAspectRatioStyle = () => {
    if (!aspectRatioInfo) return {};
    
    return {
      aspectRatio: aspectRatioInfo.ratio,
      maxWidth: '100%',
      maxHeight: '400px'
    };
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Preview</h3>
            {aspectRatioInfo && (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>{aspectRatioInfo.name}</span>
                <span className="text-gray-300">•</span>
                <span className="font-mono">{aspectRatioInfo.dimensions}</span>
              </div>
            )}
          </div>
        </div>

        {/* Image Display Area */}
        <div className="p-6">
          <div className="relative">
            {isGenerating ? (
              <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg">
                <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600 text-center">
                  Generating your marketing image...
                </p>
                <p className="text-sm text-gray-500 text-center mt-2">
                  This may take a few moments
                </p>
              </div>
            ) : imageResult ? (
              <div className="space-y-4">
                {/* Image */}
                <div 
                  className="relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                  onClick={() => setIsModalOpen(true)}
                >
                  <img
                    src={imageResult.imageUrl}
                    alt="Generated marketing image"
                    className="w-full h-auto object-contain"
                    style={getAspectRatioStyle()}
                  />
                  
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-full p-3 shadow-lg">
                      <SafeIcon icon={FiMaximize2} className="w-6 h-6 text-gray-700" />
                    </div>
                  </div>
                  
                  {/* Text Overlay Preview */}
                  {visibleOverlays.length > 0 && (
                    <div className="absolute inset-0 pointer-events-none">
                      {visibleOverlays.map((overlay) => {
                        let overlayText = overlay.text;
                        if (overlay.type === 'offer' && overlay.template && overlay.fields) {
                          overlayText = overlay.template.template;
                          overlay.template.fields.forEach(field => {
                            const value = overlay.fields[field] || `[${field}]`;
                            overlayText = overlayText.replace(`{{${field}}}`, value);
                          });
                        }

                        const getPositionClasses = (position) => {
                          const positions = {
                            'top-left': 'top-2 left-2',
                            'top-center': 'top-2 left-1/2 transform -translate-x-1/2',
                            'top-right': 'top-2 right-2',
                            'center-left': 'top-1/2 left-2 transform -translate-y-1/2',
                            'center': 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
                            'center-right': 'top-1/2 right-2 transform -translate-y-1/2',
                            'bottom-left': 'bottom-2 left-2',
                            'bottom-center': 'bottom-2 left-1/2 transform -translate-x-1/2',
                            'bottom-right': 'bottom-2 right-2'
                          };
                          return positions[position] || positions['bottom-right'];
                        };

                        const getFontSize = (size) => {
                          const sizes = {
                            'small': 'text-xs',
                            'medium': 'text-sm',
                            'large': 'text-base',
                            'xl': 'text-lg'
                          };
                          return sizes[size] || sizes['large'];
                        };

                        const getFontStyle = (style) => {
                          const styles = {
                            'bold': 'font-bold',
                            'elegant': 'font-light italic',
                            'modern': 'font-medium',
                            'classic': 'font-serif',
                            'dynamic': 'font-bold uppercase tracking-wide'
                          };
                          return styles[style] || styles['bold'];
                        };

                        return (
                          <div
                            key={overlay.id}
                            className={`absolute ${getPositionClasses(overlay.position)} ${getFontSize(overlay.fontSize)} ${getFontStyle(overlay.fontStyle)} text-white px-2 py-1 rounded shadow-lg max-w-xs`}
                            style={{
                              color: overlay.color,
                              backgroundColor: overlay.backgroundColor !== 'transparent' ? overlay.backgroundColor : undefined,
                              textShadow: overlay.backgroundColor === 'transparent' ? '1px 1px 2px rgba(0,0,0,0.8)' : undefined
                            }}
                          >
                            {overlayText || 'Sample Text'}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Status and Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {imageResult.isPlaceholder ? (
                      <div className="flex items-center space-x-2 text-amber-600">
                        <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
                        <span className="text-sm">Preview Image</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 text-green-600">
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                        <span className="text-sm">Generated</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View full size"
                    >
                      <SafeIcon icon={FiMaximize2} className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={handleCopyPrompt}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Copy prompt"
                    >
                      <SafeIcon icon={FiCopy} className="w-4 h-4" />
                    </button>
                    
                    {navigator.share && (
                      <button
                        onClick={handleShare}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Share image"
                      >
                        <SafeIcon icon={FiShare2} className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={handleDownload}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Download image"
                    >
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Additional Info */}
                {imageResult.note && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">{imageResult.note}</p>
                  </div>
                )}

                {imageResult.error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{imageResult.error}</p>
                  </div>
                )}
              </div>
            ) : (
              <div 
                className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-lg"
                style={getAspectRatioStyle()}
              >
                <SafeIcon icon={FiEye} className="w-12 h-12 text-gray-300 mb-4" />
                <p className="text-gray-600 text-center">
                  Configure your settings and click "Generate Image" to create your marketing visual
                </p>
                {visibleOverlays.length > 0 && (
                  <p className="text-sm text-gray-500 text-center mt-2">
                    {visibleOverlays.length} text overlay{visibleOverlays.length !== 1 ? 's' : ''} ready to apply
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Text Overlays Summary */}
        {visibleOverlays.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Active Text Overlays:</h4>
            <div className="space-y-1">
              {visibleOverlays.map((overlay) => {
                let overlayText = overlay.text;
                if (overlay.type === 'offer' && overlay.template && overlay.fields) {
                  overlayText = overlay.template.template;
                  overlay.template.fields.forEach(field => {
                    const value = overlay.fields[field] || `[${field}]`;
                    overlayText = overlayText.replace(`{{${field}}}`, value);
                  });
                }
                
                return (
                  <div key={overlay.id} className="flex items-center justify-between text-xs text-gray-600">
                    <span className="truncate flex-1">
                      "{overlayText || 'Sample Text'}"
                    </span>
                    <span className="ml-2 text-gray-400 capitalize">
                      {overlay.position.replace('-', ' ')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        imageResult={imageResult}
        aspectRatio={aspectRatio}
        textOverlays={textOverlays}
        prompt={prompt}
      />
    </>
  );
};

export default ImagePreview;